import { useSelector } from 'react-redux'
import moment from 'moment'
import { useDefaultRangeConfig } from '@src/hooks/useDefaultRangeConfig'
import { useTimelineChartRanges } from '@src/components/Timeline/useTimelineChartRanges'
import { isDataAvailable } from '@src/components/Timeline/utils'
import { ChartTypes } from '@src/components/Timeline/constants'
import { dailyChartsSelector } from '@src/models/dailyCharts/selectors'
import { useScoreStatistics } from '@src/screens/Events/utils/useScoreStatistics'

export const usePrimaryStatsData = ({ displayLastReading }: { displayLastReading: boolean }) => {
  const { units, summary } = useScoreStatistics()

  const { primary } = useSelector(dailyChartsSelector)
  const timelineChartRanges = useTimelineChartRanges(
    primary,
    primary.values.length > 0 && isDataAvailable(primary.values),
    ChartTypes.Glucose,
  )

  const { ranges: meanRanges = [] } = useDefaultRangeConfig('mean') ?? {}

  const mean = summary?.mean || 0

  const meanDisplayValue = mean ? `${mean}` : 'N/A'
  const meanUnits = mean ? units : ''

  if (displayLastReading) {
    const glucoseMeasurements = primary.values.filter(
      (value) => value.tag === 'Glucose' && value.y !== null,
    )

    const lastGlucoseMeasurement = glucoseMeasurements[glucoseMeasurements.length - 1] as
      | typeof glucoseMeasurements[0]
      | undefined

    const lastReadingDisplayValue =
      lastGlucoseMeasurement?.displayValue || lastGlucoseMeasurement?.y?.toString() || 'N/A'

    const lastReadingUnits =
      lastGlucoseMeasurement?.y && lastReadingDisplayValue === lastGlucoseMeasurement.y.toString()
        ? units
        : ''

    const lastReadingTime = lastGlucoseMeasurement ? moment(lastGlucoseMeasurement.x) : undefined

    return {
      primaryStatRanges: timelineChartRanges,
      primaryStatValue: lastGlucoseMeasurement?.y,
      primaryStatDisplayValue: lastReadingDisplayValue,
      primaryStatUnits: lastReadingUnits,
      primaryStatTitle: 'Last Reading',
      lastReadingTime,
    }
  }

  return {
    primaryStatRanges: meanRanges,
    primaryStatValue: mean,
    primaryStatDisplayValue: meanDisplayValue,
    primaryStatUnits: meanUnits,
    primaryStatTitle: 'Daily Average',
    lastReadingTime: undefined,
  }
}
